/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosRequestConfig } from 'axios';
import { useEffect, useState } from 'react';

import { isTokenExpired } from '../utils';
import { useAppState } from '../state/context/AppStateContext';
import { BASE_URL } from '../config';

const httpClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Global logout callback that will be set by the hook
let logoutCallback: (() => void) | null = null;

/**
 * Handles logout when a 401 response is received
 */
const handleUnauthorized = (): void => {
  localStorage.clear();
  
  if (logoutCallback) {
    logoutCallback();
  } else {
    // Fallback: redirect to login if callback not set
    window.location.href = '/login';
  }
};

// Set up global response interceptor for 401 handling
httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status || error?.status;
    if (status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

const useAxios = <T,>(reqParams?: AxiosRequestConfig) => {
  const [response, setResponse] = useState<T | undefined>(undefined);
  const [error, setError] = useState<any>();
  const [loading, setLoading] = useState(!!reqParams);

  const { authStatus, setAuthStatus } = useAppState();

  // Update global logout callback whenever setAuthStatus changes
  useEffect(() => {
    logoutCallback = () => {
      setAuthStatus?.();
    };
  }, [setAuthStatus]);

  const fetch = (params: AxiosRequestConfig, jwtToken?: string) => {
    const reqInterceptor = httpClient.interceptors.request.use(async (req) => {
      const token = jwtToken ?? authStatus?.token;
      req.headers.Authorization = `Bearer ${token}`;
      
      if (isTokenExpired(token)) {
        setAuthStatus?.();
      }
      
      return req;
    });

    setLoading(true);
    return httpClient
      .request(params)
      .then((response) => {
        setResponse(response?.data);
        return response?.data;
      })
      .catch((error) => {
        setError(error?.response?.data);
        return error?.response?.data;
      })
      .finally(() => {
        setLoading(false);
        httpClient.interceptors.request.eject(reqInterceptor);
      });
  };

  useEffect(() => {
    if (!reqParams) {
      return;
    }

    fetch(reqParams);
  }, [reqParams?.url]);

  return { response, error, loading, fetch };
};

export default useAxios;
